import { useRef } from "react";
import { Button, Icon, IconButton, useDialogBehavior } from "@astrum/ui";
import { useFileUrl } from "../lib/useFileUrl.js";
import { downloadFile } from "../lib/transport.js";
import "./document-viewer.css";

/** Types the viewer can show. Kept in one place so chat, Files and task
 *  attachments agree on what gets a "Preview" control. */
export const VIEWABLE_DOCUMENT_TYPES = new Set(["application/pdf"]);

/**
 * A full-screen view of a document attachment — today, a PDF.
 *
 * The sibling of ImageLightbox for files the browser renders with its own
 * viewer rather than as pixels. The bytes come from `/preview`, which serves
 * PDFs inline.
 */
export function DocumentViewer({
  fileId,
  fileName,
  open,
  onClose,
}: {
  fileId: string;
  fileName: string;
  open: boolean;
  onClose: () => void;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const src = useFileUrl(open ? `/api/v1/files/${fileId}/preview` : null);

  useDialogBehavior(open, onClose, containerRef);

  if (!open) return null;

  return (
    <div
      className="document-viewer"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={containerRef}
        className="document-viewer__frame"
        role="dialog"
        aria-modal="true"
        aria-label={fileName}
        tabIndex={-1}
      >
        <header className="document-viewer__head">
          <Icon name="document" size={16} />
          <span className="document-viewer__name" title={fileName}>
            {fileName}
          </span>
          <Button
            size="sm"
            variant="secondary"
            icon={<Icon name="download" size={14} />}
            onClick={() => void downloadFile(`/api/v1/files/${fileId}/download`, fileName)}
          >
            Download
          </Button>
          <IconButton icon={<Icon name="close" size={18} />} label="Close" onClick={onClose} />
        </header>
        {src ? (
          <iframe src={src} title={fileName} className="document-viewer__body" />
        ) : (
          <div className="document-viewer__loading" aria-hidden="true" />
        )}
      </div>
    </div>
  );
}
